import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/courses', label: 'Courses' },
    { path: '/collection', label: 'Collection' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#2B2B2B] shadow-lg' : 'bg-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
              <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
                {/* Circle border */}
                <circle cx="50" cy="50" r="47" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
                
                {/* Girl silhouette */}
                {/* Head */}
                <circle cx="50" cy="22" r="7" fill="#D4AF37"/>
                
                {/* Neck and upper body */}
                <rect x="47" y="29" width="6" height="8" fill="#D4AF37" rx="1"/>
                
                {/* Choli/Blouse - fitted top */}
                <path d="M45 37 L42 45 L42 52 L58 52 L58 45 L55 37 Z" fill="#D4AF37"/>
                
                {/* Waist */}
                <rect x="44" y="52" width="12" height="3" fill="#D4AF37"/>
                
                {/* Lehenga - flowing skirt */}
                <path d="M44 55 Q35 60, 28 75 L28 82 C28 84, 30 85, 32 85 L68 85 C70 85, 72 84, 72 82 L72 75 Q65 60, 56 55 Z" fill="#D4AF37"/>
                
                {/* Lehenga pleats/details */}
                <line x1="38" y1="60" x2="35" y2="78" stroke="#F8F6F0" strokeWidth="1" opacity="0.6"/>
                <line x1="44" y1="58" x2="42" y2="80" stroke="#F8F6F0" strokeWidth="1" opacity="0.6"/>
                <line x1="50" y1="57" x2="50" y2="82" stroke="#F8F6F0" strokeWidth="1" opacity="0.6"/>
                <line x1="56" y1="58" x2="58" y2="80" stroke="#F8F6F0" strokeWidth="1" opacity="0.6"/>
                <line x1="62" y1="60" x2="65" y2="78" stroke="#F8F6F0" strokeWidth="1" opacity="0.6"/>
                
                {/* Dupatta/Scarf flowing */}
                <path d="M42 40 Q38 45, 36 52" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" opacity="0.8"/>
                <path d="M58 40 Q62 45, 64 52" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" opacity="0.8"/>
                
                {/* Arms */}
                <path d="M42 45 L36 56" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M58 45 L64 56" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-[#D4AF37] font-playfair text-2xl sm:text-3xl font-bold tracking-wider leading-none">
                KRYSTAL
              </span>
              <span className="text-[#D4AF37] font-montserrat text-[10px] sm:text-xs font-light tracking-[0.2em]">
                APPAREL STORE
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-montserrat text-sm tracking-widest transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                    : 'text-[#F8F6F0] hover:text-[#D4AF37]'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label.toUpperCase()}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-[#F8F6F0] p-2"
            data-testid="mobile-menu-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#2B2B2B] border-t border-[#D4AF37]" data-testid="mobile-menu">
          <div className="px-4 pt-2 pb-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block font-montserrat text-sm tracking-widest py-2 transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#D4AF37]'
                    : 'text-[#F8F6F0] hover:text-[#D4AF37]'
                }`}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              >
                {link.label.toUpperCase()}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
